import React from "react";
import { useShallow } from "zustand/react/shallow";
import { Room, RoomState, Event } from "@uno/protocol";
import useStoreRoom from "../../../store/useStoreRoom";
import useStoreRoomPlayer from "../../../store/useStoreRoomPlayer";
import useStoreRoomScene from "../../../store/useStoreRoomScene";
import useStoreRoomSession from "../../../store/useStoreRoomSession";
import { AppContext } from "../../../context";
import { shareUnchanged } from "../../../utils/RoomState";

const useRoomStateUpdate = () => {
    const { socket } = React.useContext(AppContext);
    const setRoom = useStoreRoom(useShallow(state => state.setRoom));
    const setRoomPlayer = useStoreRoomPlayer(useShallow(state => state.setRoomPlayer));
    const refreshRoomScene = useStoreRoomScene(useShallow(state => state.refresh));

    React.useEffect(() => {
        const room = useStoreRoom.getState().room;
        const isPlayer = room?.players.some(player => player.player.socket.id === socket?.id);

        if(room?.state === RoomState.WaitingPlayers && isPlayer) {
            socket?.emit(Event.RoomPlayerIsReady, room.id);
        }
    }, [useStoreRoom(state => state.room?.state)]);

    return React.useCallback((incoming: Room & { receivedAt?: number }) => {
        const sessionRoomId = useStoreRoomSession.getState().roomId;

        if(!sessionRoomId || incoming.id !== sessionRoomId) {
            return;
        }

        const newRoom = shareUnchanged(useStoreRoom.getState().room, incoming);

        setRoom(newRoom, incoming.receivedAt);

        const currentPlayer = newRoom.players.find(player => player.player.socket.id === socket?.id);
        setRoomPlayer(currentPlayer || null);

        if(![RoomState.Playing, RoomState.Ended].includes(newRoom.state)) {
            return;
        }

        if(newRoom.state === RoomState.Ended) {
            return;
        }

        const currentCards = useStoreRoomScene.getState().cards;

        newRoom.players
            .forEach(roomPlayer => {
                const isCurrentPlayer = currentPlayer && roomPlayer.player.id === currentPlayer.player.id;
                const storedCards = currentCards[roomPlayer.player.id];
                const storedCount = storedCards?.length || 0;

                // Skip if cards haven't changed
                if(isCurrentPlayer && storedCount > 0) {
                    return;
                }

                if(!isCurrentPlayer && storedCount === roomPlayer.cards.length) {
                    return;
                }

                const cards = roomPlayer.cards.map(card => ({ id: card.id, type: isCurrentPlayer ? "BACK" : card.type, canBePut: false, isHoverable: false }));

                if(!isCurrentPlayer) {
                    for(let i = cards.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [cards[i], cards[j]] = [cards[j], cards[i]];
                    }
                }

                refreshRoomScene(roomPlayer.player.id, cards);
            });
    }, [socket]);
}

export default useRoomStateUpdate;
